import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, ArrowRight, Instagram, Facebook, Star } from 'lucide-react';

const ContactPage = () => {
  const contactEmail = 'inarchdesignstudiovashi@gmail.com';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    budget: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          access_key: 'bf531cbe-01a2-4964-b571-e0128f00ebc9',
          to: contactEmail,
          from_name: formData.name,
          subject: `New Inquiry from ${formData.name} - ${formData.service || 'General'}`,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          budget: formData.budget,
          message: formData.message,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ name: '', email: '', phone: '', service: '', budget: '', message: '' });
        }, 5000);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: MapPin, title: 'Visit Us', content: 'Office no. 220, Skylark, Plot no. 63, Shahbaz Village, Sector 11, CBD Belapur, Navi Mumbai, Maharashtra – 400614', link: 'https://maps.google.com/?q=CBD+Belapur+Navi+Mumbai' },
    { icon: Phone, title: 'Call Us', content: ['+91 8591613610', '+91 9820336742'], link: 'tel:+918591613610' },
    { icon: Mail, title: 'Email Us', content: contactEmail, link: `mailto:${contactEmail}` },
    { icon: Clock, title: 'Working Hours', content: 'Mon - Sat: 10:00 AM - 7:00 PM', link: null },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center bg-[#2C2C2C]">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=1920&q=80" alt="Contact" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 pt-40">
          <span className="text-[#D4956A] text-sm tracking-[0.2em] uppercase mb-4 block">GET IN TOUCH</span>
          <h1 className="font-serif text-5xl md:text-6xl text-white mb-6">Let's Create Something <span className="text-[#D4956A] italic">Beautiful</span></h1>
          <p className="text-white/70 text-lg max-w-2xl">Ready to transform your space? We'd love to hear from you. Reach out to start your design journey.</p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="font-serif text-3xl text-[#2C2C2C] mb-8">Contact Information</h2>
              <div className="space-y-8">
                {contactInfo.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#B87333]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="text-[#B87333]" size={24} />
                    </div>
                    <div>
                      <h4 className="font-medium text-[#2C2C2C] mb-1">{item.title}</h4>
                      {Array.isArray(item.content) ? (
                        <div className="flex flex-col">
                          {item.content.map((phone, idx) => (
                            <a key={idx} href={`tel:${phone.replace(/\s/g, '')}`} className="text-[#6B6B6B] hover:text-[#B87333] transition-colors text-sm">
                              {phone}
                            </a>
                          ))}
                        </div>
                      ) : item.link ? (
                        <a href={item.link} target={item.link.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="text-[#6B6B6B] hover:text-[#B87333] transition-colors text-sm">
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-[#6B6B6B] text-sm">{item.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div className="mt-8">
                <h4 className="font-medium text-[#2C2C2C] mb-4">Follow Us</h4>
                <div className="flex gap-4">
                  <a href="https://www.instagram.com/inarch_design_studio_belapur/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#B87333]/10 rounded-lg flex items-center justify-center hover:bg-[#B87333] hover:text-white text-[#B87333] transition-colors">
                    <Instagram size={22} />
                  </a>
                  <a href="https://www.facebook.com/people/Inarch-Design-Studio/61557602909730/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#B87333]/10 rounded-lg flex items-center justify-center hover:bg-[#B87333] hover:text-white text-[#B87333] transition-colors">
                    <Facebook size={22} />
                  </a>
                  <a href="https://www.google.com/search?q=inarchdesignstudio" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#B87333]/10 rounded-lg flex items-center justify-center hover:bg-[#B87333] hover:text-white text-[#B87333] transition-colors">
                    <Star size={22} />
                  </a>
                </div>
              </div>

              {/* Map */}
              <div className="mt-8 rounded-lg overflow-hidden h-64 bg-[#E8E3DB]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3771.5!2d73.03!3d19.02!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDAxJzEyLjAiTiA3M8KwMDEnNDguMCJF!5e0!3m2!1sen!2sin!4v1600000000000!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="InArch Location - CBD Belapur"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white p-8 md:p-10 rounded-xl shadow-lg">
                <h2 className="font-serif text-3xl text-[#2C2C2C] mb-2">Send Us a Message</h2>
                <p className="text-[#6B6B6B] mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>

                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Send className="text-green-600" size={32} />
                    </div>
                    <h3 className="font-serif text-2xl text-[#2C2C2C] mb-2">Message Sent!</h3>
                    <p className="text-[#6B6B6B]">Thank you for reaching out. We'll be in touch soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                      <div className="bg-red-50 text-red-600 p-4 rounded-lg text-sm">
                        {error}
                      </div>
                    )}
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-[#2C2C2C] font-medium mb-2">Your Name *</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 border border-[#E8E3DB] rounded-lg focus:outline-none focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] transition-colors"
                          placeholder="John Doe"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#2C2C2C] font-medium mb-2">Email Address *</label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 border border-[#E8E3DB] rounded-lg focus:outline-none focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] transition-colors"
                          placeholder="john@example.com"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-[#2C2C2C] font-medium mb-2">Phone Number</label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full px-4 py-3 border border-[#E8E3DB] rounded-lg focus:outline-none focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] transition-colors"
                          placeholder="+91 98765 43210"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-[#2C2C2C] font-medium mb-2">Service Required *</label>
                        <select
                          value={formData.service}
                          onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                          className="w-full px-4 py-3 border border-[#E8E3DB] rounded-lg focus:outline-none focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] transition-colors bg-white"
                          required
                        >
                          <option value="">Select a service</option>
                          <option value="residential">Residential Interiors</option>
                          <option value="commercial">Commercial Spaces</option>
                          <option value="villa">Luxury Villas</option>
                          <option value="modular-kitchen">Modular Kitchen</option>
                          <option value="landscape">Landscape Design</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-[#2C2C2C] font-medium mb-2">Estimated Budget</label>
                      <select
                        value={formData.budget}
                        onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                        className="w-full px-4 py-3 border border-[#E8E3DB] rounded-lg focus:outline-none focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] transition-colors bg-white"
                      >
                        <option value="">Select budget range</option>
                        <option value="5-10">₹5 Lakhs - ₹10 Lakhs</option>
                        <option value="10-25">₹10 Lakhs - ₹25 Lakhs</option>
                        <option value="25-50">₹25 Lakhs - ₹50 Lakhs</option>
                        <option value="50-100">₹50 Lakhs - ₹1 Crore</option>
                        <option value="100+">₹1 Crore+</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm text-[#2C2C2C] font-medium mb-2">Your Message *</label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-3 border border-[#E8E3DB] rounded-lg focus:outline-none focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] transition-colors h-36 resize-none"
                        placeholder="Tell us about your project, requirements, and timeline..."
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-copper py-4 text-sm tracking-wider flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'} <ArrowRight size={16} />
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[#F5F0E8]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-[#2C2C2C] mb-4">Frequently Asked Questions</h2>
            <p className="text-[#6B6B6B]">Find answers to common questions about our services</p>
          </div>
          <div className="space-y-4">
            {[
              { q: 'What areas do you serve?', a: 'We primarily serve clients across Maharashtra, including Mumbai, Navi Mumbai, Pune, Nashik, Nagpur, and 15+ other cities. We also take on select projects in other states.' },
              { q: 'How long does a typical project take?', a: 'Project timelines vary based on scope and complexity. A single room redesign may take 4-6 weeks, while a complete home or commercial space can take 3-6 months.' },
              { q: 'Do you offer virtual consultations?', a: 'Yes! We offer virtual consultations via video call for clients who cannot visit our studio. This is especially convenient for our clients in other cities.' },
              { q: 'What is your design process?', a: 'Our process includes Discovery, Concept Development, Detailed Design, and Execution. We keep you involved at every stage to ensure the final result matches your vision.' },
            ].map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-medium text-[#2C2C2C] mb-2">{faq.q}</h3>
                <p className="text-[#6B6B6B] text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
